import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Music } from "lucide-react";
import api, { categoryLabel, resolveImage } from "@/lib/api";
import { RichMultiline } from "@/lib/richText";
import Seo from "@/components/Seo";
import ShareButton from "@/components/ShareButton";
import Slideshow from "@/components/Slideshow";
import HomeVideo from "@/components/HomeVideo";

const HERO_FALLBACK =
  "https://images.pexels.com/photos/7968085/pexels-photo-7968085.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [book, setBook] = useState(null);
  const [author, setAuthor] = useState(null);
  const [hp, setHp] = useState(null);
  const [missions, setMissions] = useState(null);
  const [latestSong, setLatestSong] = useState(null);
  useEffect(() => {
    api.get("/posts?limit=6").then((r) => setPosts(r.data || [])).catch(() => {});
    api.get("/book").then((r) => setBook(r.data)).catch(() => {});
    api.get("/author").then((r) => setAuthor(r.data)).catch(() => {});
    api.get("/homepage").then((r) => setHp(r.data)).catch(() => {});
    api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
    api.get("/songs").then((r) => setLatestSong((r.data || [])[0] || null)).catch(() => {});
  }, []);

  const featured = posts.slice(0, 3);

  const HeroSection = (
    <section key="hero" className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7 rise-in">
            <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-6">
              {author?.tagline || ""}
            </p>
            <h1
              data-testid="hero-title"
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink"
            >
              <RichMultiline text={hp?.hero_headline || ""} />
            </h1>
            <p className="mt-8 text-lg text-ink-soft max-w-xl leading-relaxed">
              {hp?.hero_body || ""}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <Link
                to="/writings"
                data-testid="hero-cta-writings"
                className="inline-flex items-center gap-2 px-6 py-3 bg-terracotta text-paper text-sm tracking-wide uppercase hover:bg-ink transition-colors"
              >
                {hp?.hero_cta_primary_label || "Read the Writings"} <ArrowRight size={16} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-2 px-6 py-3 border border-ink text-ink text-sm tracking-wide uppercase hover:bg-ink hover:text-paper transition-colors"
              >
                {hp?.hero_cta_secondary_label || "About Barbara"}
              </Link>
              <ShareButton
                title="B. Ruff · Writes — Stories, poems, & essays of faith"
                excerpt={hp?.hero_body || "Writings to encourage hearts and nurture faith."}
                label="Share this site"
                url={typeof window !== "undefined" ? `${window.location.origin}/api/share/home` : undefined}
                className="inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase text-ink-soft hover:text-terracotta transition-colors"
              />
            </div>
          </div>

          <div className="lg:col-span-5 rise-in rise-delay-2">
            <div className="relative">
              {author?.photo ? (
                <img
                  src={resolveImage(author.photo)}
                  alt={author?.name || "Author"}
                  data-testid="hero-image"
                  className="w-full h-[520px] object-cover grayscale-[5%]"
                  loading="eager"
                />
              ) : (
                <div className="w-full h-[520px] bg-paper-2/40" data-testid="hero-image-placeholder" />
              )}
              {author?.quote && (
                <div className="absolute -bottom-6 -left-6 bg-paper border border-rule px-6 py-4 max-w-[230px]">
                  <p className="font-serif-display italic text-xl text-ink leading-snug">
                    "{author.quote}"
                  </p>
                  <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mt-3">
                    — {author?.quote_author?.trim()
                      ? author.quote_author
                      : (author?.name ? author.name.split(" ").map((p) => p[0]).join(".") + "." : "")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
  );

  const BookSection = (
    <section key="book" className="bg-paper-2/60 border-y border-rule">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            {book?.cover_image ? (
              <img
                src={resolveImage(book.cover_image)}
                alt={book?.title || "Book cover"}
                data-testid="featured-book-image"
                className="w-full h-[440px] object-cover"
              />
            ) : (
              <div className="w-full h-[440px] bg-paper-2/40" data-testid="featured-book-placeholder" />
            )}
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-sage mb-5">
              {hp?.featured_book_kicker || ""}
            </p>
            <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight text-ink leading-[1.1] break-words">
              {book?.title || ""}
            </h2>
            <p className="mt-6 text-base text-ink-soft leading-relaxed max-w-xl">
              {book?.tagline || ""}
            </p>
            <Link
              to="/book"
              data-testid="featured-book-cta"
              className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-terracotta link-underline"
            >
              {hp?.featured_book_cta_label || "Discover the book"} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
  );

  const WritingsSection = (
    <section key="writings" className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12 border-b border-rule pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
              {hp?.latest_kicker || "The Latest"}
            </p>
            <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight text-ink">
              {hp?.latest_heading || "From the writing desk"}
            </h2>
          </div>
          <Link
            to="/writings"
            className="hidden sm:inline-flex items-center gap-2 text-sm uppercase tracking-wide text-ink-soft hover:text-terracotta"
            data-testid="all-writings-link"
          >
            All Writings <ArrowRight size={14} />
          </Link>
        </div>

        {featured.length === 0 ? (
          <p className="text-ink-soft" data-testid="empty-featured">No writings published yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-testid="featured-grid">
            {featured.map((p, i) => (
              <Link
                to={`/writings/${p.slug}`}
                key={p.id}
                data-testid={`featured-post-${i}`}
                className="group block"
              >
                {p.cover_image && (
                  <div className="overflow-hidden mb-5 aspect-[4/5]">
                    <img
                      src={resolveImage(p.cover_image)}
                      alt={p.title}
                      className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-90"
                    />
                  </div>
                )}
                <p className="text-xs uppercase tracking-[0.2em] text-sage mb-3">
                  {categoryLabel(p.category)}
                </p>
                <h3 className="font-serif-display text-2xl text-ink leading-tight group-hover:text-terracotta transition-colors">
                  {p.title}
                </h3>
                {p.excerpt && (
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>
  );

  const SlideshowSection = <Slideshow key="slideshow" />;
  const VideoSection = <HomeVideo key="video" hp={hp} />;

  const SECTION_MAP = { video: VideoSection, slideshow: SlideshowSection, hero: HeroSection, book: BookSection, writings: WritingsSection };

  const ListenSection = latestSong ? (
    <section
      key="listen"
      data-testid="home-listen-section"
      className="bg-paper-2/60 border-y border-rule"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          {latestSong.cover_image ? (
            <img
              src={resolveImage(latestSong.cover_image)}
              alt={latestSong.title}
              data-testid="home-latest-song-cover"
              className="w-full aspect-square object-cover border border-rule"
            />
          ) : (
            <div className="w-full aspect-square bg-paper border border-rule flex items-center justify-center">
              <Music size={48} className="text-ink-soft" />
            </div>
          )}
        </div>
        <div className="lg:col-span-7">
          <p className="text-xs uppercase tracking-[0.22em] text-sage mb-5 inline-flex items-center gap-2">
            <Music size={12} /> Latest Song · Poetry set to music
          </p>
          <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight text-ink leading-[1.1]">
            {latestSong.title}
          </h2>
          {latestSong.description && (
            <p className="mt-5 text-base text-ink-soft leading-relaxed max-w-xl italic">
              {latestSong.description}
            </p>
          )}
          {latestSong.audio_url && (
            <audio
              controls
              preload="none"
              src={resolveImage(latestSong.audio_url)}
              onPlay={() => {
                api.post("/track/song", { song_id: latestSong.id, kind: "play" }).catch(() => {});
              }}
              data-testid="home-latest-song-player"
              className="w-full mt-6"
            />
          )}
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/listen"
              data-testid="home-listen-all-link"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-terracotta link-underline"
            >
              All songs <ArrowRight size={14} />
            </Link>
            {latestSong.suno_url && (
              <a
                href={latestSong.suno_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-latest-song-suno"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta"
              >
                <ExternalLink size={12} /> Listen on Suno
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  ) : null;
  if (ListenSection) SECTION_MAP.listen = ListenSection;

  const MissionsSection = (
    <section key="missions" className="bg-paper-2/40 border-y border-rule" data-testid="home-missions-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs uppercase tracking-[0.22em] text-sage mb-4">
            {missions?.kicker || "Missions & Outreach"}
          </p>
          <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight text-ink leading-[1.05]">
            <RichMultiline text={missions?.heading || "Globally Minded!"} />
          </h2>
          {missions?.description && (
            <p className="mt-6 text-base text-ink-soft leading-relaxed">
              {missions.description}
            </p>
          )}
        </div>

        {(missions?.items || []).length > 0 && (
          <div
            className={`grid gap-8 ${
              missions.items.length === 1
                ? "grid-cols-1 max-w-md mx-auto"
                : missions.items.length === 2
                ? "grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto"
                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            }`}
            data-testid="home-missions-grid"
          >
            {missions.items.map((m, i) => {
              const card = (
                <div
                  key={i}
                  data-testid={`home-mission-${i}`}
                  className="bg-paper border border-rule p-6 h-full transition-colors hover:border-ink"
                >
                  {m.image && (
                    <div className="overflow-hidden mb-4 aspect-[4/3]">
                      <img
                        src={resolveImage(m.image)}
                        alt={m.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <h3 className="font-serif-display text-2xl text-ink leading-tight">
                    {m.name}
                  </h3>
                  {m.description && (
                    <p className="mt-3 text-sm text-ink-soft leading-relaxed">
                      {m.description}
                    </p>
                  )}
                  {m.url && (
                    <span className="mt-4 inline-block text-xs uppercase tracking-[0.18em] text-terracotta link-underline">
                      Visit ↗
                    </span>
                  )}
                </div>
              );
              return m.url ? (
                <a
                  key={i}
                  href={m.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                >
                  {card}
                </a>
              ) : (
                <div key={i}>{card}</div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );

  SECTION_MAP.missions = MissionsSection;

  const order = Array.isArray(hp?.section_order) && hp.section_order.length
    ? hp.section_order.filter((s) => SECTION_MAP[s])
    : ["slideshow", "hero", "book", "writings", "listen", "missions"];
  // If an existing homepage was saved before slideshow was reorderable, append
  // it to the end as a safety fallback — but only if it isn't already there.
  // Your Section Order in Admin → Homepage now fully controls where it sits.
  if (SECTION_MAP.slideshow && !order.includes("slideshow")) order.push("slideshow");
  // Video sits right after the "From the Writing Desk" (writings) section
  if (hp?.video_url && !order.includes("video")) {
    const writingsIdx = order.indexOf("writings");
    if (writingsIdx >= 0) {
      order.splice(writingsIdx + 1, 0, "video");
    } else {
      order.push("video");
    }
  }
  // Ensure all sections appear even if missing from saved order (only those that have content)
  ["book", "hero", "writings", "listen", "missions"].forEach((s) => {
    if (SECTION_MAP[s] && !order.includes(s)) order.push(s);
  });

  return (
    <div data-testid="home-page" className="bg-paper">
      <Seo
        title={null}
        description={hp?.hero_body || "Barbara Ruffing — author, poet, and faith writer. Read her writings and discover her first children's book."}
        image={resolveImage(author?.photo)}
      />
      {order.map((s) => SECTION_MAP[s])}
    </div>
  );
}
